import React from "react";
import { useOutletContext } from "react-router-dom";
import { Award, GraduationCap, Briefcase } from "lucide-react";

export default function About() {
  const { profile } = useOutletContext() || {};
  const p = profile || {};
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C24D2C]">About</p>
        <h1 className="mt-4 font-serif-display text-5xl lg:text-6xl text-[#1A2456] font-bold">
          A scholar rooted in <span className="italic text-[#E35D29]">Madhya Pradesh.</span>
        </h1>
        <div className="grid lg:grid-cols-12 gap-14 mt-14">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#C24D2C]/20 -translate-x-4 translate-y-4" />
              <img
                src={p.photo_url || "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"}
                alt={p.name}
                className="relative w-full aspect-[4/5] object-cover object-right"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-serif-display text-3xl text-[#1A2456]">Biography</h2>
            <p className="mt-4 text-[#5C544F] leading-relaxed text-lg whitespace-pre-line">
              {p.bio || "Dr. Ruchi Singh is an Assistant Professor whose work bridges the classroom and the community."}
            </p>

            {p.research_interests?.length > 0 && (
              <>
                <h3 className="mt-10 font-serif-display text-2xl text-[#1A2456]">Research Interests</h3>
                <ul className="mt-4 flex flex-wrap gap-3">
                  {p.research_interests.map((r) => (
                    <li
                      key={r}
                      data-testid={`interest-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      className="px-4 py-2 bg-[#F5EFE6] border border-[#D8CFC4] text-sm text-[#2D2825]"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-10 grid sm:grid-cols-2 gap-6 text-sm">
              {p.email && <Info label="Email" value={p.email} />}
              {p.phone && <Info label="Phone" value={p.phone} />}
              {p.address && <Info label="Address" value={p.address} />}
              {p.institution && <Info label="Affiliation" value={p.institution} />}
            </div>
          </div>
        </div>
      </section>

      {p.education?.length > 0 && (
        <section className="bg-[#F5EFE6]/60 py-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3">
              <GraduationCap className="text-[#E35D29]" size={22} />
              <h2 className="font-serif-display text-3xl text-[#1A2456]">Education</h2>
            </div>
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {p.education.map((e, i) => (
                <div key={i} data-testid={`edu-${i}`} className="bg-white border border-[#D8CFC4] p-6">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#C24D2C]">{e.year}</p>
                  <h3 className="mt-2 font-serif-display text-xl text-[#1A2456]">{e.degree}</h3>
                  <p className="mt-1 text-sm text-[#2D2825]">{e.institution}</p>
                  {e.discipline && <p className="mt-1 text-sm text-[#5C544F] italic">{e.discipline}</p>}
                  {e.marks && <p className="mt-2 text-xs text-[#8A817C]">Score: {e.marks}</p>}
                </div>
              ))}
            </div>
            {p.experience && (
              <div className="mt-10 flex gap-4 items-start">
                <Briefcase className="text-[#E35D29] mt-1" size={20} />
                <div>
                  <h3 className="font-serif-display text-xl text-[#1A2456]">Experience</h3>
                  <p className="mt-2 text-[#5C544F] whitespace-pre-line">{p.experience}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {p.awards?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="flex items-center gap-3">
            <Award className="text-[#E35D29]" size={22} />
            <h2 className="font-serif-display text-3xl text-[#1A2456]">Awards & Recognitions</h2>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {p.awards.map((a, i) => (
              <div key={i} data-testid={`award-${i}`} className="flex gap-4 border-l-2 border-[#E35D29] pl-4 py-2">
                <span className="font-serif-display text-2xl text-[#E35D29] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[#2D2825] leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">{label}</p>
    <p className="mt-1 text-[#2D2825] break-words">{value}</p>
  </div>
);
